<script setup lang="ts">
import { isAppUsing } from '@@/bits/flip'
import { postColorTailwindClass } from '@@/bits/post_color'
import { isPostPublished, isPostScheduled } from '@@/bits/post_state'
import type { TableColumn } from '@@/bits/table_layout'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { Post } from '@@/types'
import SurfaceLazy from '@@/vuecomponents/SurfaceLazy.vue'
import SurfacePostsTableRow, { MIN_ROW_HEIGHT } from '@@/vuecomponents/SurfacePostsTableRow.vue'
import SurfacePostTableModerationBanner from '@@/vuecomponents/SurfacePostTableModerationBanner.vue'
import SurfacePostTableScheduledBanner from '@@/vuecomponents/SurfacePostTableScheduledBanner.vue'
import { storeToRefs } from 'pinia'

const props = defineProps<{
  posts: Post[]
  allColumnsToShow: TableColumn[]
  backgroundLuminance: 'light' | 'dark'
  colorScheme: 'light' | 'dark'
  commentIdsByPostId: Record<number, number[]>
  reactionIdsByPostId: Record<number, number[]>
  postBannerWidth: number
  tableWidth: number
}>()

const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const { currentUser } = storeToRefs(useSurfaceCurrentUserStore())

const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
const { getDisplayedColorForPost } = surfacePostPropertiesStore

const surfacePostsStore = useSurfacePostsStore()
const { shouldDisplayPost } = surfacePostsStore

// #region Banners
const isCurrentUserPostAuthor = (post: Post) => {
  return post.author_id === currentUser.value.id
}

const hasScheduledBanner = (post: Post) => isPostScheduled(post)

const hasModerationBanner = (post: Post) => {
  return (
    // Post is pending approval
    !isPostScheduled(post) &&
    !isPostPublished(post) &&
    // User is either moderator or post author
    (canIModerate.value || isCurrentUserPostAuthor(post))
  )
}
// #endregion

// The background class is applied to each cell but each post is a row with many cells.
// This leads to lots of duplicate checks to getDisplayedColorForPost, so cache the result by displayed color + color scheme.
const postBackgroundClassCache = new Map<string, string>()
const getPostBackgroundClass = (post: Post) => {
  const postColor = getDisplayedColorForPost(post)
  const cacheKey = `${postColor}-${props.colorScheme}`
  if (!postBackgroundClassCache.has(cacheKey)) {
    postBackgroundClassCache.set(
      cacheKey,
      postColorTailwindClass({
        postColor,
        isLightColorScheme: props.colorScheme === 'light',
      }),
    )
  }
  return postBackgroundClassCache.get(cacheKey)
}
</script>

<template>
  <div :class="['posts-table-rows flex flex-col', 'gap-y-3']">
    <template v-for="(post, index) in posts" :key="`${post.cid}-row`">
      <SurfaceLazy
        v-show="shouldDisplayPost(post.cid)"
        :data-post-cid="post.cid"
        :min-height="MIN_ROW_HEIGHT"
        :min-width="tableWidth"
        :root-margin="MIN_ROW_HEIGHT"
        :render-immediately="!isAppUsing('lazyRenderPosts')"
      >
        <SurfacePostTableScheduledBanner
          v-if="hasScheduledBanner(post)"
          :post="post"
          :color-scheme="colorScheme"
          :class="['ms-9']"
          :style="{ width: `${postBannerWidth}px` }"
          data-testid="surfacePostTableScheduledBanner"
        />

        <SurfacePostsTableRow
          :post-row-index="index"
          :post="post"
          :all-columns-to-show="allColumnsToShow"
          :background-luminance="backgroundLuminance"
          :color-scheme="colorScheme"
          :comment-ids-by-post-id="commentIdsByPostId"
          :reaction-ids-by-post-id="reactionIdsByPostId"
          :get-post-background-class="getPostBackgroundClass"
          :has-scheduled-banner="hasScheduledBanner(post)"
          :has-moderation-banner="hasModerationBanner(post)"
        />

        <SurfacePostTableModerationBanner
          v-if="hasModerationBanner(post)"
          :post="post"
          :color-scheme="colorScheme"
          :class="['ms-9']"
          :style="{ width: `${postBannerWidth}px` }"
          data-testid="surfacePostTableModerationBanner"
        />
      </SurfaceLazy>
    </template>
  </div>
</template>
